import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Trash2, Shield, User, Briefcase, Clock, Pencil, Circle } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../lib/utils";

const ROLE_OPTIONS = [
  { key: "consultant", label: "Consultant", desc: "Full CRM access — manages own leads & clients." },
  { key: "client_manager", label: "Client manager", desc: "Manages active clients only. Cannot see leads or templates." },
  { key: "admin", label: "Super admin", desc: "Everything. Users, templates, org settings, all leads & clients." },
];

const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.key, r.label]));
const EMPTY_FORM = { name: "", email: "", password: "", role: "consultant" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState({});  // user_id → summary row
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);  // user row being edited
  const [editForm, setEditForm] = useState({ name: "", role: "consultant", password: "" });

  const refresh = () => {
    setLoading(true);
    Promise.all([api.get("/users"), api.get("/users/sessions/summary?days=30")])
      .then(([usersRes, sessRes]) => {
        setUsers(usersRes.data);
        const bySid = {};
        sessRes.data.forEach((r) => (bySid[r.user_id] = r));
        setSessions(bySid);
      })
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user && user.role === "admin") refresh(); }, [user]);

  const stats = useMemo(() => {
    const c = { admin: 0, consultant: 0, client_manager: 0 };
    users.forEach((u) => (c[u.role] = (c[u.role] || 0) + 1));
    return c;
  }, [users]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  async function createUser(e) {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.email.trim() || addForm.password.length < 6) {
      toast.error("Name, email and a 6+ char password are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", addForm);
      toast.success(`${ROLE_LABEL[addForm.role]} added`);
      setAddForm(EMPTY_FORM);
      setAddOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setSaving(false);
    }
  }

  function openEdit(u) {
    setEditForm({ name: u.name, role: u.role, password: "" });
    setEditing(u);
  }

  async function saveEdit() {
    if (!editing) return;
    const payload = {};
    if (editForm.name !== editing.name) payload.name = editForm.name;
    if (editForm.role !== editing.role) payload.role = editForm.role;
    if (editForm.password) {
      if (editForm.password.length < 6) return toast.error("Password must be at least 6 characters");
      payload.password = editForm.password;
    }
    if (!Object.keys(payload).length) { setEditing(null); return; }
    setSaving(true);
    try {
      await api.put(`/users/${editing.id}`, payload);
      toast.success("User updated");
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  async function removeUser(u) {
    if (!window.confirm(`Remove ${u.name} (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User removed");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove user");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Super admin</p>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0D1B2A]">Users &amp; roles</h1>
          <p className="text-sm text-slate-500 mt-1">Add consultants, client managers, or fellow admins. Track their working hours over the last 30 days.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="add-user-button">
              <Plus className="w-4 h-4 mr-2" />Add user
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add user</DialogTitle>
            </DialogHeader>
            <form onSubmit={createUser} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="new-user-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} data-testid="new-user-email" />
              </div>
              <div className="space-y-1.5">
                <Label>Temporary password</Label>
                <Input type="text" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} data-testid="new-user-password" />
                <p className="text-xs text-slate-500">Share this so they can log in and change it later.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={addForm.role} onValueChange={(v) => setAddForm({ ...addForm, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r.key} value={r.key} data-testid={`role-option-${r.key}`}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {ROLE_OPTIONS.find((r) => r.key === addForm.role)?.desc}
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="save-user-button">
                  {saving ? "Saving…" : "Add user"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role summary */}
      <div className="grid grid-cols-3 gap-3">
        <RolePill icon={Shield} label="Super admins" count={stats.admin} tint="bg-[#0D1B2A]" testid="pill-admins" />
        <RolePill icon={User} label="Consultants" count={stats.consultant} tint="bg-[#1A7F6E]" testid="pill-consultants" />
        <RolePill icon={Briefcase} label="Client managers" count={stats.client_manager || 0} tint="bg-amber-600" testid="pill-client-managers" />
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {loading ? (
            <div className="text-sm text-slate-500 py-12 text-center">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left py-3 px-2">User</th>
                    <th className="text-left py-3 px-2">Role</th>
                    <th className="text-left py-3 px-2 hidden md:table-cell">Working hours · 30d</th>
                    <th className="text-left py-3 px-2 hidden sm:table-cell">Last login</th>
                    <th className="text-right py-3 px-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const s = sessions[u.id];
                    return (
                      <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                        <td className="py-3 px-2">
                          <p className="font-medium text-[#0D1B2A]">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </td>
                        <td className="py-3 px-2">
                          <RoleBadge role={u.role} />
                        </td>
                        <td className="py-3 px-2 hidden md:table-cell">
                          {s ? (
                            <div className="flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-slate-400" />
                              <span className="font-semibold text-[#0D1B2A]">{s.total_hours}h</span>
                              <span className="text-xs text-slate-500">· {s.session_count} sessions</span>
                              {s.open_session && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                                  <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" /> LIVE
                                </span>
                              )}
                            </div>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </td>
                        <td className="py-3 px-2 text-slate-500 hidden sm:table-cell text-xs">
                          {u.last_login_at ? formatDate(u.last_login_at) : "Never"}
                        </td>
                        <td className="py-3 px-2 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => openEdit(u)}
                            className="text-slate-600 hover:text-[#1A7F6E] text-xs font-medium inline-flex items-center gap-1 mr-2"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Pencil className="w-3 h-3" />Edit
                          </button>
                          {u.id !== user.id ? (
                            <button
                              type="button"
                              onClick={() => removeUser(u)}
                              className="text-[#DC2626] hover:underline text-xs font-medium inline-flex items-center gap-1"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-3 h-3" />Remove
                            </button>
                          ) : <span className="text-xs text-slate-400">You</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit user dialog */}
      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent data-testid="edit-user-dialog">
            <DialogHeader>
              <DialogTitle>Edit {editing.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Reset password (optional)</Label>
                <Input
                  type="text"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Leave blank to keep current"
                  data-testid="edit-user-password"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveEdit} disabled={saving} className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="save-edit-user-button">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const map = {
    admin: { cls: "bg-[#0D1B2A] text-white", icon: Shield, label: "Super admin" },
    consultant: { cls: "bg-[#E6F4F1] text-[#1A7F6E]", icon: User, label: "Consultant" },
    client_manager: { cls: "bg-amber-100 text-amber-800", icon: Briefcase, label: "Client manager" },
  };
  const cfg = map[role] || map.consultant;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${cfg.cls}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

function RolePill({ icon: Icon, label, count, tint, testid }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4 flex items-center gap-3" data-testid={testid}>
        <div className={`w-10 h-10 rounded-lg ${tint} text-white flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
          <p className="text-2xl font-semibold text-[#0D1B2A]">{count}</p>
        </div>
      </CardContent>
    </Card>
  );
}
